import { useState } from 'react'
import type { Account, Category, Transaction } from '@domain/finance'
import type { NewManualTransaction } from '@domain/ports/finance-repository'
import { parseAmountInput } from '@shared/money/parse'
import { Glass } from '@ui/glass/Glass'
import { CategoryPicker } from './CategoryPicker'
import { Botao } from '@ui/glass/Botao'

/**
 * O lançamento manual — o FAB de `Docs/04-Design/Telas/Transações.md` e a
 * "sheet de nova transação" da [[Fase 5 - Breakdown e comentários]].
 *
 * *"Cobre dinheiro em espécie, correções, e é a rede de segurança se a Pluggy
 * falhar."*
 *
 * ## Só CONTA, e a limitação é dita na tela
 *
 * Uma compra manual no cartão precisaria pertencer a uma fatura, e quem agrupa
 * faturas é a ingestão — que só enxerga o que vem da Pluggy. A transação
 * nasceria com `invoiceId: null` e o total da fatura passaria a **subestimar**,
 * em silêncio. Os três casos que a nota nomeia são todos de conta. Ver
 * `NewManualTransaction`.
 *
 * ## O sinal é ESCOLHIDO, não adivinhado
 *
 * Saída e entrada são um seletor, e o valor é sempre digitado em módulo. A
 * alternativa — deduzir pelo sinal digitado — faria "50" virar entrada e "-50"
 * saída, e ninguém digita o menos. O sinal é o campo mais consequente do
 * formulário: ele decide de que lado da soma a transação cai.
 *
 * ## Nada de `cashImpact` aqui
 *
 * A tela não escolhe campo derivado. Quem os deriva é o adapter, chamando as
 * funções do domínio; `firestore.rules` recusa o documento se eles não forem
 * coerentes com o instrumento.
 */

const METHOD_LABELS: Record<NonNullable<Transaction['source']['paymentMethod']>, string> = {
  pix: 'Pix',
  debit: 'Débito',
  credit: 'Crédito',
  boleto: 'Boleto',
  transfer: 'Transferência',
  cash: 'Dinheiro',
  other: 'Outro',
}

type Props = {
  accounts: Account[]
  categories: Category[]
  /** Hoje na casa. Injetado — o relógio mora em `shared/date` (decisão 79). */
  today: string
  onCreate: (input: NewManualTransaction) => Promise<void>
  onClose: () => void
}

export function NovaTransacao({ accounts, categories, today, onCreate, onClose }: Props) {
  const ativas = accounts.filter((a) => a.status === 'active')
  const [accountId, setAccountId] = useState(ativas[0]?.id ?? '')
  const [descricao, setDescricao] = useState('')
  const [valor, setValor] = useState('')
  const [saida, setSaida] = useState(true)
  const [data, setData] = useState(today)
  const [metodo, setMetodo] = useState<NonNullable<Transaction['source']['paymentMethod']>>('cash')
  const [categoria, setCategoria] = useState<string | null>(null)
  const [nota, setNota] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [gravando, setGravando] = useState(false)

  const salvar = () => {
    if (accountId === '') {
      setErro('escolha a conta')
      return
    }
    if (descricao.trim() === '') {
      setErro('escreva uma descrição')
      return
    }
    const parsed = parseAmountInput(valor)
    if (parsed.status !== 'ok') {
      setErro(parsed.status === 'invalid' ? parsed.message : 'informe o valor')
      return
    }
    const modulo = Math.abs(parsed.cents)
    if (modulo === 0) {
      setErro('o valor precisa ser maior que zero')
      return
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) {
      setErro('data inválida')
      return
    }

    setErro(null)
    setGravando(true)
    void onCreate({
      accountId,
      description: descricao.trim(),
      // **O sinal entra aqui, e só aqui.** O módulo digitado vira saída ou
      // entrada conforme o seletor.
      amountCents: saida ? -modulo : modulo,
      transactionDate: data,
      paymentMethod: metodo,
      categoryId: categoria,
      note: nota.trim() === '' ? null : nota.trim(),
    })
      .then(onClose)
      .catch(() => {
        setErro('não foi possível salvar agora')
      })
      .finally(() => {
        setGravando(false)
      })
  }

  return (
    <Glass level="frost" className="mb-4 p-5" as="section" aria-label="Nova transação">
      <h2 className="mb-3 text-sm font-semibold">Nova transação</h2>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="block text-xs text-[var(--text-muted)]">Conta</span>
          <select
            value={accountId}
            aria-label="Conta"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(event) => {
              setAccountId(event.target.value)
            }}
          >
            {ativas.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="block text-xs text-[var(--text-muted)]">Descrição</span>
          <input
            value={descricao}
            aria-label="Descrição"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(event) => {
              setDescricao(event.target.value)
            }}
          />
        </label>

        <fieldset className="text-sm">
          <legend className="text-xs text-[var(--text-muted)]">Valor</legend>
          <div className="flex gap-2">
            <select
              value={saida ? 'saida' : 'entrada'}
              aria-label="Saída ou entrada"
              className="rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
              onChange={(event) => {
                setSaida(event.target.value === 'saida')
              }}
            >
              <option value="saida">Saída</option>
              <option value="entrada">Entrada</option>
            </select>
            <input
              inputMode="decimal"
              value={valor}
              aria-label="Valor"
              placeholder="0,00"
              className="w-28 rounded-lg border border-[var(--border)] bg-transparent px-2 py-1 text-right"
              onChange={(event) => {
                setValor(event.target.value)
              }}
            />
          </div>
        </fieldset>

        <label className="text-sm">
          <span className="block text-xs text-[var(--text-muted)]">Data</span>
          <input
            type="date"
            value={data}
            aria-label="Data"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(event) => {
              setData(event.target.value)
            }}
          />
        </label>

        <label className="text-sm">
          <span className="block text-xs text-[var(--text-muted)]">Método</span>
          <select
            value={metodo}
            aria-label="Método"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(event) => {
              setMetodo(event.target.value as NonNullable<Transaction['source']['paymentMethod']>)
            }}
          >
            {Object.entries(METHOD_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <div className="text-sm">
          <span className="block text-xs text-[var(--text-muted)]">Categoria</span>
          <CategoryPicker
            categories={categories}
            value={categoria}
            ariaLabel="Categoria da nova transação"
            onChange={setCategoria}
          />
        </div>

        <label className="text-sm sm:col-span-2">
          <span className="block text-xs text-[var(--text-muted)]">Observação</span>
          <input
            value={nota}
            aria-label="Observação da nova transação"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
            onChange={(event) => {
              setNota(event.target.value)
            }}
          />
        </label>
      </div>

      {/* A limitação dita na tela, e não escondida. Quem procurar o cartão aqui
          precisa saber por que ele não está. */}
      <p className="mt-3 text-xs text-[var(--text-muted)]">
        Lançamento manual é sempre em conta. Compra no cartão vem do banco, pela
        sincronização.
      </p>

      <div className="mt-3 flex gap-3">
        <Botao tom="primario" type="button" disabled={gravando} onClick={salvar}>
          Salvar
        </Botao>
        <Botao type="button" onClick={onClose}>
          Cancelar
        </Botao>
      </div>

      {erro !== null && (
        <p role="alert" className="mt-2 text-sm text-[var(--text-muted)]">
          {erro}
        </p>
      )}
    </Glass>
  )
}
